import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import { useAuth } from '../../contexts/AuthContext';
import { canManageMfaPolicy } from '../../lib/role';
import BusinessMfaPolicyPanel from './mfa/BusinessMfaPolicyPanel';
import { useLanguage } from '../../contexts/LanguageContext';
import { LANGUAGE_OPTIONS } from '../../i18n/translations';
import { auth } from '../../lib/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Settings as SettingsIcon, Bell, Users, Zap, Shield, Save, RefreshCw, Plus } from 'lucide-react';
import IntegrationsManager from './IntegrationsManager';
import FleetManager from './Fleet/FleetManager';
import { tenantHeaders } from '../../lib/tenantSession';

const INVITE_ROLES = ['operator', 'manager', 'analyst', 'director', 'executive', 'it', 'admin'];
// Canonical IANA time zones from the platform (NOT an invented list). Falls back to a minimal common set
// only on runtimes lacking Intl.supportedValuesOf. The current stored value is always preserved as an option.
const TIMEZONE_OPTIONS = (() => {
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch (e) { /* fall through */ }
  return ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
          'America/Toronto', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Africa/Lagos',
          'Asia/Dubai', 'Asia/Kolkata', 'Asia/Shanghai', 'Asia/Tokyo', 'Australia/Sydney'];
})();
const API = process.env.REACT_APP_BACKEND_URL;
// Settings tabs — used to honor a deep-link ?tab= (e.g. from Integration Hub).
const VALID_SETTINGS_TABS = ['general', 'notifications', 'ai', 'security', 'users', 'integrations', 'fleet'];

const Settings = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentBusiness } = useBusiness();
  const { user } = useAuth();
  const brandId = currentBusiness?.brandId;
  // DR-017: tenant MFA policy management is visible to Admin/IT only. Every other role keeps
  // personal MFA (via the app-wide BusinessMfaBoundary) and sees no governance controls here.
  const showMfaPolicy = canManageMfaPolicy(user?.role);

  // Open the tab requested via ?tab= (Integration Hub deep-links ?tab=integrations).
  const _initialTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    VALID_SETTINGS_TABS.includes(_initialTab) ? _initialTab : 'general'
  );
  useEffect(() => {
    const q = searchParams.get('tab');
    if (q && VALID_SETTINGS_TABS.includes(q)) setActiveTab(q);
  }, [searchParams]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingAI, setSavingAI] = useState(false);
  const [settings, setSettings] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [aiConfig, setAiConfig] = useState(null);
  const [security, setSecurity] = useState(null);
  // Reset baseline: a deep snapshot of the last successfully loaded/saved tenant settings. Reset restores
  // THIS locally (no API call, no mutation) so it works deterministically even if a re-fetch would fail.
  const savedSnapshotRef = useRef(null);

  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('operator');
  const [inviting, setInviting] = useState(false);

  // Danger zone: self-service request to delete THIS staff member's own account/membership
  // (scope business_membership). This is NOT a business/tenant closure. We never claim the
  // account is deleted and we never log the user out — the backend verifies and completes async.
  const [deletionRequesting, setDeletionRequesting] = useState(false);
  const [deletionRequested, setDeletionRequested] = useState(false);

  const getToken = async () =>
    (auth.currentUser ? await auth.currentUser.getIdToken() : null) || localStorage.getItem('crm_access_token') || '';
  const authHdr = async () => ({ Authorization: `Bearer ${await getToken()}`, ...tenantHeaders() });

  const loadStaff = useCallback(async () => {
    if (!brandId) return;
    setStaffLoading(true);
    try {
      const r = await fetch(`${API}/api/v1/billing/staff/by-brand/${brandId}`, { headers: await authHdr() });
      if (r.ok) setStaffList((await r.json()).staff || []);
    } catch (_) { /* honest empty state */ }
    finally { setStaffLoading(false); }
  }, [brandId]);

  const loadAll = useCallback(async () => {
    if (!brandId) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const h = await authHdr();
      const [s, sec, ai] = await Promise.all([
        fetch(`${API}/api/v1/tenants/${brandId}/settings`, { headers: h }),
        fetch(`${API}/api/v1/tenants/${brandId}/security-status`, { headers: h }),
        fetch(`${API}/api/v1/ai-agent/config/${brandId}`, { headers: h }),
      ]);
      if (!s.ok) throw new Error(t('st.settings.errLoadSettings', { status: s.status }));
      const sj = await s.json();
      setSettings(sj.settings);
      setNotifications(sj.notifications);
      // capture the reset baseline (deep copy so later edits never mutate the snapshot)
      savedSnapshotRef.current = JSON.parse(JSON.stringify({ settings: sj.settings, notifications: sj.notifications }));
      setSecurity(sec.ok ? await sec.json() : null);
      setAiConfig(ai.ok ? await ai.json() : null);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [brandId]);

  useEffect(() => { loadAll(); loadStaff(); }, [loadAll, loadStaff]);

  // Header "Save Changes" = tenant settings ONLY (General + Notifications). Single source, single PUT.
  const handleSave = async () => {
    if (!brandId) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/v1/tenants/${brandId}/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...(await authHdr()) },
        body: JSON.stringify({ general: settings, notifications }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || t('st.settings.errSaveFailed', { status: r.status }));
      const savedSettings = d.settings ?? settings;
      const savedNotifs = d.notifications ?? notifications;
      if (d.settings) setSettings(d.settings);
      if (d.notifications) setNotifications(d.notifications);
      // move the reset baseline forward to the values that were just persisted
      savedSnapshotRef.current = JSON.parse(JSON.stringify({ settings: savedSettings, notifications: savedNotifs }));
      toast({ title: t('st.settings.toast.saved'), description: t('st.settings.toast.savedDesc') });
    } catch (e) {
      toast({ title: t('st.settings.toast.saveFailed'), description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  // AI tab has its OWN save = ai-config ONLY. Single source, minimal payload (no overwrite of thresholds/rules).
  const handleSaveAI = async () => {
    if (!brandId) return;
    setSavingAI(true);
    try {
      const r = await fetch(`${API}/api/v1/ai-agent/config/${brandId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...(await authHdr()) },
        body: JSON.stringify({ auto_resolution_enabled: aiConfig?.auto_resolution_enabled !== false }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || t('st.settings.errSaveFailed', { status: r.status })); }
      const fresh = await fetch(`${API}/api/v1/ai-agent/config/${brandId}`, { headers: await authHdr() });
      if (fresh.ok) setAiConfig(await fresh.json());
      toast({ title: t('st.settings.toast.aiSaved'), description: t('st.settings.toast.aiSavedDesc') });
    } catch (e) {
      toast({ title: t('st.settings.toast.saveFailed'), description: e.message, variant: 'destructive' });
    } finally { setSavingAI(false); }
  };

  // Reset = restore the last loaded/saved values LOCALLY (RESET_TO_LAST_SAVED_VALUES). No API call, no
  // persisted mutation, clears the transient error. Falls back to a re-fetch only if no snapshot exists yet.
  const handleReset = () => {
    const snap = savedSnapshotRef.current;
    if (!snap) { loadAll(); return; }
    setSettings(JSON.parse(JSON.stringify(snap.settings)));
    setNotifications(JSON.parse(JSON.stringify(snap.notifications)));
    setError(null);
  };

  const handleInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast({ title: t('st.settings.toast.invalidEmail'), description: t('st.settings.toast.invalidEmailDesc'), variant: 'destructive' });
      return;
    }
    setInviting(true);
    try {
      const r = await fetch(`${API}/api/v1/billing/staff/invite`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...(await authHdr()) },
        body: JSON.stringify({ brand_id: brandId, invites: [{ email, role: inviteRole }] }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || t('st.settings.errInviteFailed', { status: r.status }));
      const res = (d.results || [])[0];
      if (res && res.email_sent === false) throw new Error(res.error || t('st.settings.inviteNotSent'));
      toast({ title: t('st.settings.toast.inviteSent'), description: t('st.settings.toast.inviteSentDesc', { email }) });
      setInviteEmail(''); setInviteRole('operator'); setInviteOpen(false);
      await loadStaff();
    } catch (e) {
      toast({ title: t('st.settings.toast.inviteFailed'), description: e.message, variant: 'destructive' });
    } finally { setInviting(false); }
  };

  // Request deletion of the current user's own staff account/membership (scope business_membership).
  // Reuses the existing bearer + tenant auth pattern. On success we show a TRUTHFUL pending state:
  // the request was received and verified, the user is notified on completion, and some records may be
  // retained where legally required. We do NOT claim completion and we do NOT sign the user out.
  const handleRequestAccountDeletion = async () => {
    if (!window.confirm(
      'Request deletion of your own Servisfy staff account and membership? This does NOT close the business or tenant. ' +
      'The request is submitted for verification; you will be notified when it completes.'
    )) return;
    setDeletionRequesting(true);
    try {
      const idempotencyKey = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `del-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const r = await fetch(`${API}/api/v1/account/deletion-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey, ...(await authHdr()) },
        body: JSON.stringify({ scope: 'business_membership', confirm: true }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Request failed (${r.status})`);
      setDeletionRequested(true);
      toast({
        title: 'Account deletion request received',
        description: 'Your request was received and verified. You will be notified when it completes. Some records may be retained where legally required.',
      });
    } catch (e) {
      toast({ title: 'Account deletion request failed', description: e.message, variant: 'destructive' });
    } finally { setDeletionRequesting(false); }
  };

  if (loading) return <div className="flex-1 p-6 text-gray-500">{t('st.settings.loading')}</div>;
  if (error) return (
    <div className="flex-1 p-6"><Card><CardContent className="py-10 text-center text-red-600">{error}
      <div className="mt-4"><Button variant="outline" onClick={loadAll}>{t('common.retry')}</Button></div>
    </CardContent></Card></div>
  );
  // Null-safe guard: settings/notifications are null until loadAll() populates them. If a
  // tenant could not be resolved (brandId null) loadAll() returns early and they stay null —
  // rendering the form below would throw (e.g. settings.display_name on null). Show a truthful
  // fallback instead of crashing or a blank page.
  if (!settings || !notifications) return (
    <div className="flex-1 p-6"><Card><CardContent className="py-10 text-center text-gray-600">
      {brandId
        ? t('st.settings.unavailable')
        : t('st.settings.noWorkspace')}
      <div className="mt-4"><Button variant="outline" onClick={loadAll}>{t('common.retry')}</Button></div>
    </CardContent></Card></div>
  );

  const setG = (k, v) => setSettings((p) => ({ ...p, [k]: v }));
  const tog = (k) => setSettings((p) => ({ ...p, [k]: !p[k] }));
  const togN = (k) => setNotifications((p) => ({ ...p, [k]: !p[k] }));

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      <div className="mb-8 flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-gray-900">{t('common.settings')}</h1><p className="text-gray-600">{t('st.settings.manageWorkspace')}</p></div>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={handleReset} disabled={saving}><RefreshCw className="h-4 w-4 mr-2" />{t('st.settings.reset')}</Button>
          <Button className="bg-gradient-to-r from-green-500 to-blue-600" onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />{saving ? t('st.savingEllipsis') : t('st.saveChanges')}</Button>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          setActiveTab(v);
          const p = new URLSearchParams(searchParams);
          p.set('tab', v);
          setSearchParams(p, { replace: true });
        }}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="general">{t('st.settings.tab.general')}</TabsTrigger>
          <TabsTrigger value="notifications">{t('st.settings.tab.notifications')}</TabsTrigger>
          <TabsTrigger value="ai">{t('st.settings.tab.ai')}</TabsTrigger>
          <TabsTrigger value="security">{t('st.settings.tab.security')}</TabsTrigger>
          <TabsTrigger value="users">{t('st.settings.tab.users')}</TabsTrigger>
          <TabsTrigger value="integrations">{t('st.integrations')}</TabsTrigger>
          <TabsTrigger value="fleet">Fleet</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          <Card><CardHeader><CardTitle className="flex items-center gap-2"><SettingsIcon className="h-5 w-5" />{t('st.settings.tab.general')}</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2"><Label>{t('st.settings.displayName')}</Label>
                  <Input value={settings.display_name ?? ''} onChange={(e) => setG('display_name', e.target.value)} /></div>
                <div className="space-y-2"><Label htmlFor="workspace-timezone">{t('st.settings.timezone')}</Label>
                  <select id="workspace-timezone" aria-label={t('st.settings.timezone')}
                    className="w-full border rounded-md h-10 px-3 bg-white"
                    value={settings.timezone ?? ''} onChange={(e) => setG('timezone', e.target.value)}>
                    {!settings.timezone && <option value="">{t('st.settings.timezonePh')}</option>}
                    {/* preserve a legacy/unknown stored zone as a safe fallback — never silently overwrite */}
                    {settings.timezone && !TIMEZONE_OPTIONS.includes(settings.timezone) && (
                      <option value={settings.timezone}>{settings.timezone}</option>
                    )}
                    {TIMEZONE_OPTIONS.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                  </select></div>
                <div className="space-y-2"><Label htmlFor="ai-default-language">{t('st.settings.aiDefaultLang')}</Label>
                  <select id="ai-default-language" aria-label={t('st.settings.aiDefaultLang')}
                    className="w-full border rounded-md h-10 px-3 bg-white"
                    value={settings.language ?? ''} onChange={(e) => setG('language', e.target.value)}>
                    {/* Preserve a legacy/unknown stored value as a safe fallback option — never silently overwrite. */}
                    {settings.language && !LANGUAGE_OPTIONS.some((l) => l.code === settings.language) && (
                      <option value={settings.language}>{settings.language}</option>
                    )}
                    {LANGUAGE_OPTIONS.map((l) => (
                      <option key={l.code} value={l.code}>{l.name} — {l.nativeName}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500">{t('st.settings.aiLangHelp')}</p></div>
                <div className="space-y-2"><Label>{t('st.settings.archiveDays')}</Label>
                  <Input type="number" min="0" value={settings.archive_after_days ?? 0}
                    onChange={(e) => setG('archive_after_days', parseInt(e.target.value || '0', 10))} />
                  <p className="text-xs text-gray-500">{t('st.settings.archiveHelp')}</p></div>
              </div>
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div><div className="font-medium">{t('st.settings.callRecording')}</div>
                  <div className="text-sm text-gray-600">{t('st.settings.callRecordingDesc')}</div>
                  <div className="text-xs text-gray-500 mt-1">{t('st.settings.callRecordingNote')}</div></div>
                <Button variant={settings.voice_recording ? 'default' : 'outline'} size="sm" onClick={() => tog('voice_recording')}
                  className={settings.voice_recording ? 'bg-gradient-to-r from-green-500 to-blue-600' : ''}>{settings.voice_recording ? t('st.enabled') : t('st.disabled')}</Button>
              </div>
            </CardContent></Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6">
          <Card><CardHeader><CardTitle className="flex items-center gap-2"><Bell className="h-5 w-5" />{t('st.settings.tab.notifications')}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {[['email', t('st.settings.emailNotif'), t('st.settings.emailNotifDesc')],
                ['sms', t('st.settings.smsNotif'), t('st.settings.smsNotifDesc')]].map(([k, l, d]) => (
                <div key={k} className="flex items-center justify-between p-4 border rounded-lg">
                  <div><div className="font-medium">{l}</div><div className="text-sm text-gray-600">{d}</div></div>
                  <Button variant={notifications[k] ? 'default' : 'outline'} size="sm" onClick={() => togN(k)}
                    className={notifications[k] ? 'bg-gradient-to-r from-green-500 to-blue-600' : ''}>{notifications[k] ? t('st.enabled') : t('st.disabled')}</Button>
                </div>))}
              <p className="text-xs text-gray-500">{t('st.settings.transactionalNote')}</p>
            </CardContent></Card>
        </TabsContent>

        <TabsContent value="ai" className="space-y-6">
          <Card><CardHeader><CardTitle className="flex items-center gap-2"><Zap className="h-5 w-5" />{t('st.settings.tab.ai')}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div><div className="font-medium">{t('st.settings.aiAutoRes')}</div>
                  <div className="text-sm text-gray-600">{t('st.settings.aiAutoResDesc')}</div></div>
                <Button variant={aiConfig?.auto_resolution_enabled !== false ? 'default' : 'outline'} size="sm"
                  onClick={() => setAiConfig((p) => ({ ...(p || {}), auto_resolution_enabled: !(p?.auto_resolution_enabled !== false) }))}
                  className={aiConfig?.auto_resolution_enabled !== false ? 'bg-gradient-to-r from-green-500 to-blue-600' : ''}>
                  {aiConfig?.auto_resolution_enabled !== false ? t('st.enabled') : t('st.disabled')}</Button>
              </div>
              <div className="flex gap-3">
                <Button onClick={handleSaveAI} disabled={savingAI}>{savingAI ? t('st.savingEllipsis') : t('st.settings.saveAi')}</Button>
                <Button variant="outline" onClick={() => navigate('/business/auto-resolution')}>{t('st.settings.manageAutoRules')}</Button>
              </div>
            </CardContent></Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          {showMfaPolicy && <BusinessMfaPolicyPanel />}
          <Card><CardHeader><CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5" />{t('st.settings.securityCompliance')}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span>{t('st.settings.auditLogging')}</span>
                <Badge className={security?.audit_logging?.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                  {security?.audit_logging?.status === 'active' ? t('st.settings.auditActive', { count: security.audit_logging.events_recorded }) : t('st.settings.auditNone')}</Badge>
              </div>
              <div className="p-3 border rounded-lg">
                <div className="font-medium mb-1">{t('st.settings.compliance')}</div>
                {security?.attestations?.length
                  ? security.attestations.map((a) => (<div key={a.name} className="flex justify-between text-sm"><span>{a.name}</span><span>{a.status}</span></div>))
                  : <div className="text-sm text-gray-500">{t('st.settings.notPublished')}</div>}
              </div>
            </CardContent></Card>

          {/* Danger zone — self-service request to delete the current user's OWN staff account/membership.
              Scope business_membership (NOT a business/tenant closure). Additive; no new route/page. */}
          <Card className="border-red-200">
            <CardHeader><CardTitle className="flex items-center gap-2 text-red-700"><Shield className="h-5 w-5" />Danger zone</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start justify-between gap-4 p-4 border border-red-200 rounded-lg bg-red-50/50">
                <div>
                  <div className="font-medium text-red-700">Request account deletion</div>
                  <div className="text-sm text-gray-600">
                    Submit a request to delete your own Servisfy staff account and membership in this workspace.
                    This does not close the business or tenant and does not remove other team members.
                  </div>
                </div>
                <Button variant="destructive" size="sm" disabled={deletionRequesting || deletionRequested}
                  onClick={handleRequestAccountDeletion}>
                  {deletionRequesting ? 'Submitting…' : deletionRequested ? 'Request submitted' : 'Request account deletion'}
                </Button>
              </div>
              {deletionRequested && (
                <div className="text-sm text-gray-700 p-3 border border-amber-200 rounded-lg bg-amber-50">
                  Your deletion request was received and verified. It is being processed and you will be notified when it
                  completes. Some records may be retained where legally required. Your account remains active until the
                  request is completed.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-6">
          <Card><CardHeader><CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" />{t('st.settings.tab.users')}</CardTitle></CardHeader>
            <CardContent className="text-center py-12">
              <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-6">{t('st.settings.manageTeam')}</p>
              <Dialog open={usersOpen} onOpenChange={setUsersOpen}>
                <DialogTrigger asChild><Button className="bg-gradient-to-r from-blue-500 to-purple-600"><Users className="h-4 w-4 mr-2" />{t('st.settings.manageUsers')}</Button></DialogTrigger>
                <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{t('st.settings.teamMembers')}</DialogTitle><DialogDescription>{t('st.settings.inviteReview')}</DialogDescription></DialogHeader>
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <h4 className="text-lg font-medium">{t('st.settings.teamMembers')}</h4>
                      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                        <DialogTrigger asChild><Button variant="outline"><Plus className="h-4 w-4 mr-2" />{t('st.settings.addUser')}</Button></DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader><DialogTitle>{t('st.settings.inviteStaffMember')}</DialogTitle><DialogDescription>{t('st.settings.onboardingEmail')}</DialogDescription></DialogHeader>
                          <div className="space-y-4">
                            <div className="space-y-2"><Label>{t('st.settings.email')}</Label>
                              <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder={t('st.settings.emailPh')} /></div>
                            <div className="space-y-2"><Label>{t('st.settings.role')}</Label>
                              <select className="w-full border rounded-md h-10 px-3" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                                {INVITE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select></div>
                            <Button className="w-full" onClick={handleInvite} disabled={inviting}>{inviting ? t('st.settings.sending') : t('st.settings.sendInvite')}</Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <div className="space-y-4">
                      {staffLoading ? (<div className="text-center py-4 text-gray-500">{t('common.loading')}</div>)
                        : staffList.length === 0 ? (<div className="text-center py-4 text-gray-400">{t('st.settings.noTeamMembers')}</div>)
                        : staffList.map((u) => (
                          <div key={u.id} className="flex items-center justify-between p-4 border rounded-lg">
                            <div><div className="font-medium">{u.name || '—'}</div><div className="text-sm text-gray-500">{u.phone || u.email || '—'}</div></div>
                            <div className="flex items-center space-x-3">
                              <Badge className="bg-blue-100 text-blue-800 capitalize">{u.role || t('st.settings.staffRole')}</Badge>
                              <Badge className={u.status !== 'inactive' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>{u.status === 'inactive' ? t('st.inactive') : t('st.active')}</Badge>
                            </div>
                          </div>))}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent></Card>
        </TabsContent>

        <TabsContent value="integrations" className="space-y-6"><IntegrationsManager /></TabsContent>
        <TabsContent value="fleet" className="space-y-6"><FleetManager /></TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
